import { useEffect, useState } from 'react';

import { LIST_BUCKET } from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';

/**
 * A list a page reads from its build-time seed (src/lib/prefetch.ts) on the
 * first render, so hydration matches the static HTML, and fetches in the
 * browser only when the seed isn't on the page (a client-side navigation, the
 * dev server). `fetcher` must ask what the build asked.
 */
export function useSeededList<T>(seedKey: string, fetcher: () => Promise<T[]>) {
  const [rows, setRows] = useState<T[] | null>(() => getPrefetched<T[]>(LIST_BUCKET, seedKey) ?? null);

  useEffect(() => {
    if (rows) return;
    let cancelled = false;
    fetcher()
      .then((fetched) => {
        if (!cancelled) setRows(fetched);
      })
      .catch(() => {
        // An empty list rather than an error: the page is complete without it.
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
    // Fetched once per seed key; `rows` and `fetcher` change only through here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  return { rows: rows ?? [], loading: rows === null };
}
